import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Ban, CheckCircle2, Edit, KeyRound, Plus, Save, ShieldCheck, Trash2, X } from "lucide-react";

const EMPTY_CREATE = { email: "", name: "", password: "" };

function ConfirmModal({ title, description, confirmLabel = "Confirmar", confirmVariant = "primary", onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="eyebrow">Confirmação</div>
        <h2 className="font-heading text-2xl mt-1">{title}</h2>
        <p className="text-sm text-[var(--ink-soft)] mt-3">{description}</p>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onCancel} className="pill-btn pill-ghost" data-testid="admin-user-cancel-confirm">Cancelar</button>
          <button
            onClick={onConfirm}
            className={`pill-btn ${confirmVariant === "danger" ? "pill-primary !bg-[var(--terracotta)] hover:!bg-[var(--terracotta)]" : "pill-primary"}`}
            data-testid="admin-user-confirm-btn"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/admin/users").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  const submitCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", createForm);
      toast.success("Administrador criado");
      setCreating(false);
      setCreateForm(EMPTY_CREATE);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name: editing.name, email: editing.email };
      if (editing.password) payload.password = editing.password;
      await api.patch(`/admin/users/${editing.id}`, payload);
      toast.success("Administrador atualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/block`, { is_blocked: !u.is_blocked });
      toast.success(u.is_blocked ? "Administrador desbloqueado" : "Administrador bloqueado");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendReset = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(`Email de reposição enviado para ${u.email}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Administrador eliminado");
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setConfirm(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Gestão</div>
          <h1 className="font-heading text-5xl mt-2">Utilizadores</h1>
          <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-2xl">
            Administradores da plataforma. Só administradores conseguem aceder ao backoffice CARING · Referências.
          </p>
        </div>
        <button
          onClick={() => { setCreateForm(EMPTY_CREATE); setCreating(true); }}
          className="pill-btn pill-primary"
          data-testid="admin-users-create-btn"
        >
          <Plus className="w-4 h-4"/> Novo administrador
        </button>
      </div>

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--muted)] text-left">
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Autenticação</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const isMe = me?.id === u.id;
              return (
                <tr key={u.id} className="border-t border-[var(--border)]" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-[var(--forest)]"/>
                      {u.name || "—"}
                      {isMe && <span className="chip chip-blue text-[10px]">Você</span>}
                    </div>
                    <div className="text-xs text-[var(--ink-soft)] mt-0.5">
                      Criado a {u.created_at ? new Date(u.created_at).toLocaleDateString("pt-PT") : "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{u.email}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className="chip chip-neutral">{u.auth_provider === "google" ? "Google" : "Palavra-passe"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`chip ${u.is_blocked ? "chip-terra" : "chip-green"}`}>
                      {u.is_blocked ? (<><Ban className="w-3 h-3"/> Bloqueado</>) : (<><CheckCircle2 className="w-3 h-3"/> Ativo</>)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <button
                        onClick={() => setEditing({ id: u.id, name: u.name || "", email: u.email, password: "" })}
                        className="pill-btn pill-ghost !py-1.5 !px-3"
                        data-testid={`admin-user-edit-${u.id}`}
                      >
                        <Edit className="w-3.5 h-3.5"/> Editar
                      </button>
                      {u.auth_provider !== "google" && (
                        <button
                          onClick={() => sendReset(u)}
                          className="pill-btn pill-ghost !py-1.5 !px-3"
                          data-testid={`admin-user-reset-${u.id}`}
                        >
                          <KeyRound className="w-3.5 h-3.5"/> Repor pass
                        </button>
                      )}
                      <button
                        onClick={() => toggleBlock(u)}
                        disabled={isMe}
                        title={isMe ? "Não pode bloquear-se a si próprio" : ""}
                        className="pill-btn pill-ghost !py-1.5 !px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`admin-user-block-${u.id}`}
                      >
                        <Ban className="w-3.5 h-3.5"/> {u.is_blocked ? "Desbloquear" : "Bloquear"}
                      </button>
                      <button
                        onClick={() => setConfirm({
                          title: "Eliminar administrador?",
                          description: `Esta ação é irreversível. ${u.name || u.email} deixará de conseguir aceder ao backoffice CARING.`,
                          confirmLabel: "Eliminar",
                          confirmVariant: "danger",
                          onConfirm: () => remove(u),
                        })}
                        disabled={isMe}
                        title={isMe ? "Não pode eliminar-se a si próprio" : ""}
                        className="pill-btn pill-ghost !py-1.5 !px-3 text-[var(--terracotta)] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`admin-user-delete-${u.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5"/> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--ink-soft)]">Ainda não existem administradores.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setCreating(false)}>
          <form onSubmit={submitCreate} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 flex items-center justify-between border-b border-[var(--border)]">
              <div>
                <div className="eyebrow">Novo administrador</div>
                <h2 className="font-heading text-2xl">Criar acesso</h2>
              </div>
              <button type="button" onClick={() => setCreating(false)} className="pill-btn pill-ghost !py-2 !px-3" data-testid="admin-user-close-create"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium">Nome</label>
                <input required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="admin-user-create-name"/>
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <input required type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="admin-user-create-email"/>
              </div>
              <div>
                <label className="text-sm font-medium">Palavra-passe inicial</label>
                <input required type="text" minLength={8} value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className={`mt-1.5 ${inputCls}`} placeholder="Mínimo 8 caracteres" data-testid="admin-user-create-password"/>
                <p className="text-xs text-[var(--ink-soft)] mt-1">Comunique esta password ao administrador. Pode ser alterada depois em "Repor pass".</p>
              </div>
            </div>
            <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="pill-btn pill-ghost">Cancelar</button>
              <button disabled={busy} className="pill-btn pill-primary" data-testid="admin-user-create-submit">
                <Save className="w-4 h-4"/> {busy ? "A criar…" : "Criar"}
              </button>
            </div>
          </form>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={submitEdit} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 flex items-center justify-between border-b border-[var(--border)]">
              <div>
                <div className="eyebrow">Editar administrador</div>
                <h2 className="font-heading text-2xl">{editing.email}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="pill-btn pill-ghost !py-2 !px-3" data-testid="admin-user-close-edit"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium">Nome</label>
                <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="admin-user-edit-name"/>
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <input required type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="admin-user-edit-email"/>
              </div>
              <div>
                <label className="text-sm font-medium">Nova palavra-passe (opcional)</label>
                <input type="text" minLength={8} value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} className={`mt-1.5 ${inputCls}`} placeholder="Deixe em branco para não alterar" data-testid="admin-user-edit-password"/>
              </div>
            </div>
            <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className="pill-btn pill-ghost">Cancelar</button>
              <button disabled={busy} className="pill-btn pill-primary" data-testid="admin-user-edit-submit">
                <Save className="w-4 h-4"/> {busy ? "A guardar…" : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          description={confirm.description}
          confirmLabel={confirm.confirmLabel}
          confirmVariant={confirm.confirmVariant}
          onCancel={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
        />
      )}
    </div>
  );
}
